import ApiService from './ApiService';
import {
  GetWebSessionResponse,
  GetPaymentMethodsResponse,
  GetInvoicesResponse,
  GetSubscriptionResponse,
  GetInvoiceDetailsResponse,
  GetInvoiceDownloadResponse,
  GetPricesResponse,
} from '@customTypes/billing';
import { getPromoterCode } from '@utils/promoterCode';

interface CreateCheckoutSessionResponse {
  url: string;
}

export const BILLING_SESSION_EXPIRED_ERROR = 'BILLING_SESSION_EXPIRED';

export const isBillingSessionExpiredError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.message.includes(BILLING_SESSION_EXPIRED_ERROR);
};

// Declare Rewardful global type
declare global {
  interface Window {
    Rewardful?: {
      referral?: string;
    };
  }
}

class BillingService {
  private buildBillingError(error?: string, status?: number): Error {
    const normalized_error = error?.toLowerCase() || '';
    const session_expired =
      status === 401 ||
      normalized_error.includes('access token is missing') ||
      normalized_error.includes('access token verification failed') ||
      normalized_error.includes('refresh token not found') ||
      normalized_error.includes('invalid refresh token state');

    if (session_expired) {
      return new Error(
        `${BILLING_SESSION_EXPIRED_ERROR}: Your session expired. Please sign in again.`,
      );
    }

    return new Error(error || 'Billing action failed');
  }

  // Get Stripe web session
  public async getWebSession(): Promise<GetWebSessionResponse> {
    const { success, data, error, status } =
      await ApiService.get<GetWebSessionResponse>(
        `/stripe/billing-portal-session`,
      );

    if (success && data) {
      return data;
    } else {
      throw this.buildBillingError(error, status);
    }
  }

  // Create Stripe checkout session
  public async createCheckoutSession(
    priceId?: string,
  ): Promise<CreateCheckoutSessionResponse> {
    const queryParams: Record<string, string> = {};

    if (priceId) {
      queryParams.price_id = priceId;
    }

    // Pass Rewardful referral ID for affiliate tracking
    const referralId = window.Rewardful?.referral;
    if (referralId) {
      queryParams.referral = referralId;
    }

    const promoterCode = getPromoterCode();
    if (promoterCode) {
      queryParams.promoter_code = promoterCode;
    }

    const { success, data, error, status } =
      await ApiService.get<CreateCheckoutSessionResponse>(
        `/stripe/checkout-session`,
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
      );

    if (success && data) {
      return data;
    } else {
      throw this.buildBillingError(error, status);
    }
  }

  // Get payment methods
  public async getPaymentMethods(): Promise<GetPaymentMethodsResponse> {
    const { success, data, error } =
      await ApiService.get<GetPaymentMethodsResponse>(
        `/stripe/payment-methods`,
      );

    if (success && data) {
      return data;
    } else {
      throw new Error(error || 'Failed to fetch payment methods');
    }
  }

  // Get invoices
  public async getInvoices(): Promise<GetInvoicesResponse> {
    const { success, data, error } =
      await ApiService.get<GetInvoicesResponse>(`/stripe/invoices`);

    if (success && data) {
      return data;
    } else {
      throw new Error(error || 'Failed to fetch invoices');
    }
  }

  // Get subscription
  public async getSubscription(): Promise<GetSubscriptionResponse> {
    const { success, data, error } =
      await ApiService.get<GetSubscriptionResponse>(`/stripe/subscription`);

    if (success && data) {
      return data;
    } else {
      throw new Error(error || 'Failed to fetch subscription');
    }
  }

  // Get invoice details
  public async getInvoiceDetails(
    invoiceId: string,
  ): Promise<GetInvoiceDetailsResponse> {
    const { success, data, error } =
      await ApiService.get<GetInvoiceDetailsResponse>(
        `/stripe/invoices/${invoiceId}`,
      );

    if (success && data) {
      return data;
    } else {
      throw new Error(error || 'Failed to fetch invoice details');
    }
  }

  // Change subscription plan
  public async changePlan(priceId: string): Promise<{ message: string }> {
    const { success, data, error, status } =
      await ApiService.post<{ price_id: string }, { message: string }>(`/stripe/change-plan`, {
        price_id: priceId,
      });

    if (success && data) {
      return data;
    } else {
      throw this.buildBillingError(error, status);
    }
  }

  // Get available plan prices
  public async getPrices(): Promise<GetPricesResponse> {
    const { success, data, error } =
      await ApiService.get<GetPricesResponse>(`/stripe/prices`);

    if (success && data) {
      return data;
    } else {
      throw new Error(error || 'Failed to fetch prices');
    }
  }

  // Get invoice PDF download URL
  public async getInvoiceDownloadUrl(
    invoiceId: string,
  ): Promise<GetInvoiceDownloadResponse> {
    const { success, data, error } =
      await ApiService.get<GetInvoiceDownloadResponse>(
        `/stripe/invoices/${invoiceId}/download`,
      );

    if (success && data) {
      return data;
    } else {
      throw new Error(error || 'Failed to fetch invoice download URL');
    }
  }
}

export default new BillingService();
